import { Dispatch, SetStateAction } from 'react';
import { AdsFilteringType, TopAgentListItem, UserModel, UserSettingsResponseType } from '@/types';
import { LocationModel } from '@/types/location';
import type { TopAgentFilterValue } from '../utils/adsSearchParams';

import { predefinedVars } from './AdsFilters.init';
import styles from './AdsFilters.module.scss';
import { DiscountBanner } from './Banners/DiscountBanner';
import { FilterActionsRow, FilterPrimaryActions } from './FilterActionsRow';
import { MoreFiltersDialog } from './MoreFiltersDialog';
import { PrimarySearchRow } from './PrimarySearchRow';
import { PropertyKindSelector } from './PropertyKindSelector';
import { useAdsFiltersController } from './useAdsFiltersController';
import { TopAgentFilter } from './TopAgentFilter';
import type { AdsFilterChangeOptions } from './filterTypes';

interface AdsFiltersProps {
  filteringObj: AdsFilteringType;
  inputAddressValue: string;
  isMyAds?: boolean;
  isRent?: boolean;
  locations: LocationModel[];
  onChangeFilters: (
    value: AdsFilteringType,
    shouldSendRequest?: boolean,
    options?: AdsFilterChangeOptions
  ) => void;
  onTopAgentChange?: (value: TopAgentFilterValue) => void;
  selectedLocation: LocationModel | null;
  setChangeFilter: (value: boolean) => void;
  setInputAddressValue: (value: string) => void;
  setLocations: Dispatch<SetStateAction<LocationModel[]>>;
  setSelectedLocation: (value: LocationModel | null) => void;
  user: UserModel;
  userSettings: UserSettingsResponseType | null;
  enableTopAgentFilter?: boolean;
  topAgent?: TopAgentFilterValue;
  topAgents?: TopAgentListItem[];
  topAgentsError?: boolean;
  topAgentsLoaded?: boolean;
  topAgentsLoading?: boolean;
}

export const AdsFilters = ({
  filteringObj,
  onChangeFilters,
  locations,
  inputAddressValue,
  setInputAddressValue,
  setSelectedLocation,
  selectedLocation,
  setChangeFilter,
  isRent,
  user,
  userSettings,
  enableTopAgentFilter = false,
  onTopAgentChange,
  topAgent,
  topAgents = [],
  topAgentsError = false,
  topAgentsLoaded = false,
  topAgentsLoading = false,
}: AdsFiltersProps) => {
  const { derived, handlers, moreFiltersLocation, state } = useAdsFiltersController({
    filteringObj,
    inputAddressValue,
    isRent,
    onChangeFilters,
    selectedLocation,
    setChangeFilter,
    setInputAddressValue,
    setSelectedLocation,
    user,
    userSettings,
    topAgent: enableTopAgentFilter ? topAgent : undefined,
  });
  const isQueryParams = true;
  const hasTopAgentFilter = enableTopAgentFilter && Boolean(onTopAgentChange);

  return (
    <>
      <div className={styles.filtersContainer}>
        <div className={styles.filtersPanel}>
          <PrimarySearchRow
            inputAddressValue={inputAddressValue}
            isRent={isRent}
            locations={locations}
            onInputAddressChange={handlers.handleInputAddressChange}
            onInputAddressClear={() => handlers.handleInputAddressClear(isQueryParams)}
            onLocationSelect={(value) => handlers.onSelectLocation(value, isQueryParams)}
            onRentFrequencyChange={(value) =>
              handlers.handleChangeRentFrequency(value, 'rentFrequency', isQueryParams)
            }
            onTransactionTypeChange={(value) =>
              handlers.handleChangeTransactionType(value, 'transactionType', isQueryParams)
            }
            options={derived.rentSalesData}
            propertyKind={
              <PropertyKindSelector
                onPropertyKindChange={(value) =>
                  handlers.handleChangePropertyKind(value, 'propertyKind', isQueryParams)
                }
                onTabChange={state.setPropertyTypeTab}
                options={predefinedVars.propertyKindValues}
                propertyTypeTab={state.propertyTypeTab}
                selectedPropertyKinds={state.filterState.propertyKind}
                valueLabel={derived.propertyKindValueLabel}
              />
            }
            rentFrequency={state.filterState.rentFrequency}
            selectedLocation={selectedLocation}
            setInputAddressValue={setInputAddressValue}
            transactionType={state.filterState.transactionType}
          />
          <div
            className={`${styles.quickFiltersGrid} ${
              hasTopAgentFilter ? '' : styles.quickFiltersGridWithoutTopAgent
            }`}
            data-has-top-agent={hasTopAgentFilter}
            data-testid='quick-filters-grid'
          >
            <FilterActionsRow
              areaLabel={derived.areaLabel}
              areaMax={state.filterState.size.areaMax}
              areaMin={state.filterState.size.areaMin}
              areaUnit={userSettings?.areaUnits.toLowerCase()}
              bedsLabel={derived.bedsBathsText}
              bedsOptions={predefinedVars.bedsValues}
              isAreaRangeInvalid={derived.isAreaRangeInvalid}
              isPriceRangeInvalid={derived.isPriceRangeInvalid}
              onAreaBlur={handlers.handleCommitBasicSize}
              onBedsChange={(value) => handlers.handleChangeBeds(value, 'beds', isQueryParams)}
              onMaximumAreaChange={(event) =>
                handlers.handleChangeSize(event, 'areaMax', isQueryParams)
              }
              onMaximumPriceChange={(event) =>
                handlers.handleChangePrice(event, 'priceMax', isQueryParams)
              }
              onMinimumAreaChange={(event) =>
                handlers.handleChangeSize(event, 'areaMin', isQueryParams)
              }
              onMinimumPriceChange={(event) =>
                handlers.handleChangePrice(event, 'priceMin', isQueryParams)
              }
              onPriceBlur={handlers.handleCommitBasicPrice}
              priceLabel={derived.priceLabel}
              priceMax={state.filterState.price.priceMax}
              priceMin={state.filterState.price.priceMin}
              selectedBeds={state.filterState.beds}
            />
            {hasTopAgentFilter && onTopAgentChange ? (
              <div className={styles.topAgentControl} data-testid='quick-filter-top-agent'>
                <TopAgentFilter
                  error={topAgentsError}
                  isLoaded={topAgentsLoaded}
                  isLoading={topAgentsLoading}
                  onChange={onTopAgentChange}
                  topAgents={topAgents}
                  value={topAgent}
                />
              </div>
            ) : null}
            <FilterPrimaryActions
              activeFiltersCount={derived.counterFilters}
              onMoreFiltersOpen={handlers.handleOpenFilterDialog}
              onReset={handlers.handleResetFilters}
            />
          </div>
          {state.openFilterDialog && state.moreFiltersDraft && (
            <MoreFiltersDialog
              open={state.openFilterDialog}
              onClose={handlers.handleCloseDialog}
              setOpenDialog={state.setOpenFilterDialog}
              setInputAddressValue={moreFiltersLocation.setInputAddressValue}
              locations={moreFiltersLocation.locations}
              selectedLocation={moreFiltersLocation.selectedLocation}
              inputAddressValue={moreFiltersLocation.inputAddressValue}
              onInputChange={handlers.handleMoreFiltersInputAddressChange}
              handleClear={handlers.handleMoreFiltersInputAddressClear}
              onSelect={handlers.onSelectMoreFiltersLocation}
              isRent={isRent}
              filterState={state.moreFiltersDraft}
              handleChangeTransactionType={handlers.handleChangeTransactionType}
              handleChangeRentFrequency={handlers.handleChangeRentFrequency}
              handleChangeCompletionStatus={handlers.handleChangeCompletionStatus}
              handleChangePropertyKind={handlers.handleChangePropertyKind}
              handleChangeBeds={handlers.handleChangeBeds}
              handleChangeBaths={handlers.handleChangeBaths}
              handleChangeFurnished={handlers.handleChangeFurnished}
              handleChangeRenovation={handlers.handleChangeRenovation}
              handleChangeFloor={handlers.handleChangeFloor}
              handleChangeOccupancy={handlers.handleChangeOccupancy}
              handleChangePostHandover={handlers.handleChangePostHandover}
              handleChangeHandover={handlers.handleChangeHandover}
              handleChangePrice={handlers.handleChangePrice}
              handleChangeSize={handlers.handleChangeSize}
              handleChangeDownPayment={handlers.handleChangeDownPayment}
              handleChangeKeywords={handlers.handleChangeKeywords}
              handleChangeCreator={handlers.handleChangeCreator}
              handleChangeMyAgency={handlers.handleChangeMyAgency}
              handleChangeMyFriend={handlers.handleChangeMyFriend}
              handleShowResult={handlers.handleShowResult}
              handleResetFilters={handlers.handleResetFilters}
              isTariffFree={derived.isTariffFree}
              userSettings={userSettings}
            />
          )}
        </div>
      </div>
      {derived.showBillingUi && derived.isTariffFree && (
        <DiscountBanner hasSubscriptions={derived.hasSubscriptions} />
      )}
    </>
  );
};
